import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { MonitorSmartphone, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorMessage, useApi } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import type { Screen } from "@/lib/types";
import { EmptyState, Loading, PageHeader } from "@/components/app/PageHeader";
import { StatusPill, screenState } from "@/components/app/StatusPill";
import { ConfirmAction } from "@/components/app/ConfirmAction";
import { ReplaceScreenDialog } from "@/components/app/ReplaceScreenDialog";

export default function ScreensPage() {
  const { data, loading, reload } = useApi<Screen[]>("/screens");
  const [replacing, setReplacing] = useState<Screen | null>(null);
  const remove = async (s: Screen) => { try { await api.delete(`/screens/${s.id}`); toast.success(`Écran « ${s.name} » supprimé`); reload(); } catch (e) { toast.error(errorMessage(e)); } };
  const { openPairing } = useOutletContext<{ openPairing: () => void }>();
  const pairBtn = <Button data-testid="btn-open-pairing-modal" onClick={openPairing} className="bg-blue-600 text-white hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Jumeler un nouvel écran</Button>;

  return (
    <div>
      <PageHeader eyebrow="Parc" title="Écrans et téléviseurs" description="Chaque TV Android jumelée reçoit un jeton opaque et télécharge son manifest." actions={pairBtn} />
      {loading && !data ? <Loading /> : !data?.length ? (
        <EmptyState testId="screens-empty" icon={<MonitorSmartphone className="h-6 w-6" />} title="Aucun écran" text="Lancez l'APK Innova-Sign sur une TV Android, puis saisissez le code affiché." action={pairBtn} />
      ) : (
        <div className="panel overflow-x-auto">
          <Table data-testid="screens-table">
            <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead>Nom</TableHead><TableHead>Statut</TableHead><TableHead>Groupe</TableHead><TableHead>Fuseau</TableHead><TableHead>Dernier contact</TableHead><TableHead>Version</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((s) => (
                <TableRow key={s.id} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell><Link to={`/ecrans/${s.id}`} data-testid={`screen-row-link-${s.id}`} className="font-medium text-slate-100 hover:text-blue-300">{s.name}</Link><p className="font-mono text-[10px] text-slate-600">{s.id}</p></TableCell>
                  <TableCell><StatusPill state={screenState(s)} testId={`screen-status-badge-${s.id}`} /></TableCell>
                  <TableCell className="text-slate-400">{s.group_name || "—"}{s.use_group_config && <span className="ml-1 text-[10px] text-cyan-400">(config. groupe)</span>}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{s.timezone}</TableCell>
                  <TableCell className="text-slate-400">{fmtRelative(s.last_seen_at)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{s.manifest_version || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" data-testid={`screen-replace-${s.id}`} onClick={() => setReplacing(s)} className="text-slate-400 hover:text-orange-300"><RefreshCw className="mr-1 h-4 w-4" />Remplacer</Button>
                      <ConfirmAction testId={`screen-delete-${s.id}`} title={`Supprimer « ${s.name} » ?`} description="Le téléviseur sera déconnecté et l'écran supprimé définitivement avec sa configuration." confirmLabel="Supprimer" onConfirm={() => remove(s)}
                        trigger={<Button variant="ghost" size="icon" data-testid={`screen-delete-btn-${s.id}`} aria-label="Supprimer" className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {replacing && <ReplaceScreenDialog screen={replacing} open={!!replacing} onOpenChange={(o) => !o && setReplacing(null)} onDone={reload} />}
    </div>
  );
}
